import cn from 'clsx'
import React, { useCallback } from 'react'
import Checkbox from 'components/ui/checkbox'
import Icon, { IconKey } from 'components/ui/icon'
import styles from './index.module.css'

type Props = {
  caption: React.ReactNode
  checked?: ('checked' | 'partial') | null | undefined
  depth: number | null | undefined
  disabled?: boolean
  expandable?: boolean
  expanded?: boolean
  icon?: IconKey
  selectable?: boolean
  onClick?: () => void
  onExpand?: () => void
}

/**
 * A tree item component that displays hierarchical data with expand/collapse and selection functionality
 * @param caption - The main content/label for the tree item
 * @param checked - The selection state ('checked', 'partial', or null/undefined)
 * @param depth - The nesting depth of the item for indentation
 * @param disabled - Whether the item is disabled
 * @param expandable - Whether the item can be expanded/collapsed
 * @param expanded - Whether the item is currently expanded
 * @param icon - Optional icon to display
 * @param selectable - Whether the item can be selected with a checkbox
 * @param onClick - Callback called when the item is clicked
 * @param onExpand - Callback called when the expand/collapse button is clicked
 * @returns A tree item with hierarchical display and interaction capabilities
 */
const TreeItem = ({
  caption,
  checked,
  depth,
  disabled,
  expandable,
  expanded,
  icon,
  selectable,
  onClick,
  onExpand,
}: Props) => {
  const indentNodes: React.ReactNode[] = []

  if (depth) {
    for (let i = 0; i < depth; i++) {
      indentNodes.push(<div className={styles.treeItemIndent} key={i} />)
    }
  }

  const handleExpand = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.stopPropagation()

      if (onExpand) {
        onExpand()
      }
    },
    [onExpand],
  )
  return (
    <div
      className={cn(styles.treeItem, {
        [styles.treeItemDisabled]: disabled,
      })}
      onClick={onClick}
    >
      <div className={styles.treeItemBefore}>
        {indentNodes}
        <div
          className={cn(styles.treeItemExpand, {
            [styles.treeItemExpandDisabled]: disabled,
          })}
          onClick={expandable && !disabled ? handleExpand : undefined}
        >
          {expandable && (
            <Icon type={expanded ? 'arrow-down' : 'arrow-right'} />
          )}
        </div>
        {selectable && (
          <div className={styles.treeItemCheckbox}>
            <Checkbox
              checked={!disabled && Boolean(checked)}
              disabled={disabled}
              partial={checked === 'partial'}
              onChange={onClick}
            />
          </div>
        )}
        {icon && (
          <div className={styles.treeItemIcon}>
            <Icon type={icon} />
          </div>
        )}
      </div>
      <div className={styles.treeItemCaption}>{caption}</div>
    </div>
  )
}

export default React.memo<Props>(TreeItem)
